// ContactForm — public enquiry form. POSTs to PATHS.contact (no auth); the
// server saves a lead + emails the team. Used inline on the landing page and
// standalone on /contact. Includes a honeypot field (`website`) for bot
// rejection and a success state.

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CircleCheck, Send } from '@/lib/icons';
import { PATHS } from '@/lib/paths';
import { apiPublic, apiErrorMessage } from '@/lib/api';

export function ContactForm({ source = 'landing' }: { source?: string }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await apiPublic(PATHS.contact, {
        method: 'POST',
        body: { name, email, company, message, source, website },
      });
      setDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
          <CircleCheck className="h-6 w-6 text-emerald-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">Thanks — we'll be in touch.</h3>
        <p className="mt-2 text-sm text-white/55">
          Your enquiry landed with the team. We usually reply within a business day at{' '}
          <span className="text-white/80">{email}</span>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot — hidden from humans, catches bots. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Lee" className="bg-white/[0.03]" />
        </Field>
        <Field label="Work email" required>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" className="bg-white/[0.03]" />
        </Field>
      </div>

      <Field label="Studio / company">
        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Games" className="bg-white/[0.03]" />
      </Field>

      <Field label="What are you building?" required>
        <Textarea
          required
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          placeholder="Tell us about your game, your team, and what you'd like Bugpunch to help with."
          className="resize-none bg-white/[0.03]"
        />
      </Field>

      {error && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      <Button
        type="submit"
        disabled={submitting}
        className="group h-11 w-full bg-[#f97316] font-semibold text-white hover:bg-[#ea6a0c]"
      >
        {submitting ? 'Sending…' : 'Request access'}
        {!submitting && <Send className="ml-1 h-4 w-4" />}
      </Button>
      <p className="text-center text-xs text-white/35">
        No spam. We only use your details to reply to this enquiry.
      </p>
    </form>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-white/55">
        {label}
        {required && <span className="ml-0.5 text-[#f97316]">*</span>}
      </Label>
      {children}
    </div>
  );
}
